// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useLocalRuntime, AssistantRuntimeProvider } from '@assistant-ui/react';
import { Thread, FilesContext, MessageFilesContext, SharedThreadContext, EmbeddedConfigContext, LocalFilesContext, Office365Context, GmailContext, GptSelectorContext, QuestionAutoSubmitListener, IncognitoContext } from './assistant-ui/thread';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, CircularProgress, Typography, Avatar } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ProfileModal } from './ProfileModal';
import { SearchChatsModal } from './SearchChatsModal';
import { ConversationStarters } from './ConversationStarters';
import { WelcomeGuide } from './WelcomeGuide';
import { ShareModal } from './ShareModal';
import chatEvents from '../lib/chatEvents';
import {
  isLocalFilesSupported,
  connectFolder,
  disconnectFolder,
  getConnectedFolder,
  executeLocalTool,
  prepareLocalWrite,
  waitForWriteDecision,
  applyLocalWrite,
  setThreadFolderTrusted,
} from '../lib/localFiles';

import { VoiceProvider, useVoice } from '../lib/voice';

// Separate component that contains the runtime hook - only rendered after loading
function ChatRuntimeWrapper({ children, threadIdRef, selectedGptId, selectedFiles, setSelectedFiles, setMessageFiles, loadThreads, API_URL, token, onFolderTrusted, localFolderRef, onThreadTitle, onThreadId, unavailableAssistantRef, incognitoRef }) {
  // Use refs to always get the CURRENT value of props inside the async generator
  // This is critical because the generator closure captures stale values otherwise
  const selectedFilesRef = useRef(selectedFiles);
  const setSelectedFilesRef = useRef(setSelectedFiles);
  const selectedGptIdRef = useRef(selectedGptId);
  const onFolderTrustedRef = useRef(onFolderTrusted);

  // Keep refs in sync with props
  useEffect(() => {
    selectedFilesRef.current = selectedFiles;
  }, [selectedFiles]);

  useEffect(() => {
    setSelectedFilesRef.current = setSelectedFiles;
  }, [setSelectedFiles]);

  useEffect(() => {
    selectedGptIdRef.current = selectedGptId;
  }, [selectedGptId]);

  useEffect(() => {
    onFolderTrustedRef.current = onFolderTrusted;
  }, [onFolderTrusted]);

  const runtime = useLocalRuntime({
    initialMessages: [], // Always start fresh - history is rendered separately
    async *run({ messages, abortSignal }) {
      // Whether the backend sent its explicit 'done' event. Without it the
      // stream was SEVERED (server restart/crash/network drop) and whatever
      // is on screen is a stale partial — the app re-fetches the thread.
      let sawDone = false;
      const notifySevered = () => {
        if (!abortSignal?.aborted) {
          chatEvents.dispatchEvent(
            new CustomEvent('stream_severed', { detail: { threadId: threadIdRef.current } })
          );
        }
      };
      try {
        const lastMessage = messages[messages.length - 1];
        const userContent = lastMessage.content.find(c => c.type === 'text')?.text || '';

        // Thread whose assistant is no longer accessible (share revoked):
        // the composer is read-only, but stale UIs / racing sends must not
        // hit the backend — answer locally instead of failing silently.
        if (unavailableAssistantRef?.current) {
          yield { content: [{ type: 'text', text: `🔒 **${unavailableAssistantRef.current.name || 'This assistant'}** is no longer available to you. Start a new chat to continue.` }] };
          return;
        }

        // Get the CURRENT files from the ref (not the stale closure value)
        const filesToSend = [...selectedFilesRef.current];

        // Clear files immediately so the UI updates right away. Set the ref
        // synchronously too — state updates are async, and a second send
        // before re-render would re-attach the same files.
        selectedFilesRef.current = [];
        setSelectedFilesRef.current([]);

        const currentFiles = filesToSend.map(file => ({
          id: URL.createObjectURL(file),
          filename: file.name,
          content_type: file.type,
          size: file.size
        }));

        // Key by the runtime message id — UserMessage looks its files up by
        // id, so history messages and deletions can never misalign it.
        setMessageFiles(prev => ({ ...prev, [lastMessage.id]: currentFiles }));

        const formData = new FormData();
        const payload = {
          type: 'items.create',
          params: {
            thread_id: threadIdRef.current,
            message: userContent,
            custom_gpt_id: selectedGptIdRef.current || undefined,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            // Browser language — used to localize search results etc. server-side
            language: (navigator.language || 'en').split('-')[0],
            // Incognito: marks NEW threads — hidden from history/search,
            // excluded from memory (server ignores it on existing threads)
            ...(incognitoRef?.current ? { incognito: true } : {}),
            // Local folder state: name when connected+granted, else null.
            // The server offers the local_* tools ONLY when this is set.
            local_folder: localFolderRef?.current && !localFolderRef.current.needsPermission
              ? (localFolderRef.current.name || '')
              : null
          }
        };

        formData.append('payload', JSON.stringify(payload));
        filesToSend.forEach((file, index) => {
          formData.append(`file_${index}`, file);
        });

        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Local Files: POST a tool result back to the backend while the chat
        // stream is still open (resolves the Future the agent loop awaits)
        const sendLocalToolResult = async (callId, payload) => {
          const post = () =>
            fetch(`${API_URL}/cme`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                type: 'local_tool.result',
                params: { call_id: callId, ...payload },
              }),
            });
          try {
            let res = await post();
            if (!res.ok) {
              // One retry: transient network/5xx failures must not leave the
              // agent loop waiting for the full 60s timeout.
              console.warn(`local_tool.result HTTP ${res.status} for ${callId}, retrying once...`);
              await new Promise((r) => setTimeout(r, 1000));
              res = await post();
              if (!res.ok) console.error(`local_tool.result failed after retry (${res.status}) for ${callId}`);
            }
          } catch (e) {
            console.error('Failed to send local tool result:', e);
            try {
              await post();
            } catch (e2) {
              console.error('local_tool.result retry failed:', e2);
            }
          }
        };

        // Notify plugins (avatar, etc.) that user is sending a message
        chatEvents.dispatchEvent(new CustomEvent('user_message'));

        const response = await fetch(`${API_URL}/cme`, {
          method: 'POST',
          headers,
          body: formData,
          signal: abortSignal
        });

        if (!response.ok) {
          if (response.status === 401) {
            // Token expired/invalid - clear and redirect to login
            localStorage.removeItem('cme_token');
            localStorage.removeItem('cme_user');
            window.location.href = '/';
            return;
          }
          if (response.status === 429) {
            // Rate limited — show a friendly message instead of failing silently.
            // NOTE: the local runtime only renders YIELDED updates (a generator's
            // return value is discarded), so this MUST be yielded.
            let detail = '';
            try {
              const body = await response.json();
              detail = body.error || body.detail || '';
            } catch { /* body not JSON */ }
            let text;
            if (/concurrent/i.test(detail)) {
              text = `**⏳ ${detail}**\n\nA previous response is likely still streaming or got stuck after a connection drop. It clears by itself within a few minutes — or immediately if you press Stop / reload.`;
            } else {
              text = `**⏳ Rate limit reached.** ${detail || 'Please wait a few seconds and try again.'}`;
            }
            yield { content: [{ type: 'text', text }] };
            return;
          }
          if (response.status === 403) {
            // Assistant access revoked (share removed) or thread not ours —
            // surface the backend's friendly message.
            let detail = '';
            try {
              const body = await response.json();
              detail = body.error || body.detail || '';
            } catch { /* body not JSON */ }
            yield { content: [{ type: 'text', text: `🔒 ${detail || 'This conversation is no longer available.'}` }] };
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        // "Thinking… Xs" counts from the start of the turn, like the saved
        // duration (models think before the first reasoning text arrives)
        const runStartedAt = Date.now();
        let buffer = '';
        let accumulatedText = '';
        let currentStatus = null;
        // Track all non-text content parts (images, charts, file downloads) to preserve them
        let accumulatedParts = [];
        // The turn's activity: ONE block at the top of the message — what is
        // happening now (the latest status), and a timeline of the real
        // actions (status events marked `step`) and, for GPTs that show it,
        // the reasoning. It is "done" (collapsed) as soon as anything else
        // shows up after it; a later action or reasoning reopens it.
        let activityPart = null;
        let activityMark = null; // {text, parts} sizes when activity last grew
        const touchActivity = () => {
          if (!activityPart) {
            activityPart = { type: 'activity', timeline: [], current: null, startedAt: runStartedAt, endedAt: null };
          }
          return activityPart;
        };
        const growActivity = () => {
          activityPart.endedAt = null;
          activityMark = { text: accumulatedText.length, parts: accumulatedParts.length };
        };

        // Freeze the currently-streaming text into its own part (so parts stay
        // in CHRONOLOGICAL order — e.g. text before a write card stays above
        // it, and text streamed after the card lands BELOW it)
        const flushTextToParts = () => {
          if (!accumulatedText) return;
          let displayText = chatEvents.transformText(accumulatedText);
          displayText = displayText.replace(/\[\[[^\]]*$/, '');
          accumulatedParts.push({ type: 'text', text: displayText });
          accumulatedText = '';
        };

        // Helper to build content array with all accumulated parts.
        // Frozen parts first (chronological), then the text streaming right now.
        const buildContentParts = (includeStatus = null) => {
          const parts = [];
          if (activityPart) {
            if (!activityPart.endedAt && (
              (activityMark && (accumulatedText.length > activityMark.text || accumulatedParts.length > activityMark.parts))
              || (!activityMark && (accumulatedText || accumulatedParts.length))
            )) {
              activityPart.endedAt = Date.now();
            }
            parts.push({ ...activityPart, timeline: activityPart.timeline.map(item => ({ ...item })) });
          }
          parts.push(...accumulatedParts);
          if (accumulatedText) {
            // Transform + strip any trailing incomplete tags (e.g. "[[emot" at end of stream chunk)
            let displayText = chatEvents.transformText(accumulatedText);
            displayText = displayText.replace(/\[\[[^\]]*$/, '');
            parts.push({ type: 'text', text: displayText });
          }
          if (includeStatus) {
            parts.push(includeStatus);
          }
          return parts;
        };

        while (true) {
          // Stop must take effect BETWEEN events too (not just during a
          // pending read): break out and cancel the stream body.
          if (abortSignal?.aborted) {
            try { reader.cancel(); } catch { /* already closed */ }
            break;
          }
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim() || !line.startsWith('data: ')) continue;

            try {
              const jsonStr = line.slice(6);
              const parsed = JSON.parse(jsonStr);

              if (parsed.type === 'reasoning') {
                // Streamed reasoning (batched by the server), labelled with
                // the GPT that reasons (a delegated specialist's comes too).
                if (parsed.text) {
                  const act = touchActivity();
                  const source = parsed.source || '';
                  // A thought continues its source's latest item when that is
                  // a thought, even across parallel specialists' items — one
                  // reasoning stream is never cut mid-sentence
                  const mine = [...act.timeline].reverse().find(item => item.source === source);
                  if (mine && mine.kind === 'thought') mine.text += parsed.text;
                  else act.timeline.push({ kind: 'thought', source, text: parsed.text });
                  growActivity();
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'status_event') {
                // Status events - shown immediately as the activity's "now"
                // line; the ones marked `step` (real actions) join the timeline
                if (parsed.content && parsed.content[0]) {
                  const statusType = parsed.content[0].type || 'log';
                  const statusText = parsed.content[0].text;
                  currentStatus = { type: statusType, text: statusText };
                  const act = touchActivity();
                  // The "now" line names the latest real action: generic
                  // statuses ("Done", "Taking action...") only fill it before
                  // the first action; errors always show.
                  if (parsed.step || statusType === 'error' || !act.timeline.some(item => item.kind === 'action')) {
                    act.current = statusText;
                  }
                  if (parsed.step) {
                    const source = parsed.source || '';
                    const last = act.timeline[act.timeline.length - 1];
                    if (!(last && last.kind === 'action' && last.text === statusText && last.source === source)) {
                      act.timeline.push({ kind: 'action', source, text: statusText });
                    }
                    growActivity();
                  }
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'thread_stream') {
                // Check for different content types in thread_stream
                const textContent = parsed.content.find(c => c.type === 'text');
                const imageContent = parsed.content.find(c => c.type === 'image');

                if (imageContent && imageContent.data) {
                  // Image content - add to accumulated parts and show immediately
                  currentStatus = null;
                  accumulatedParts.push({
                    type: 'image',
                    data: imageContent.data,
                    mime_type: imageContent.mime_type || 'image/png'
                  });
                  yield { content: buildContentParts() };
                } else if (textContent && textContent.text) {
                  chatEvents.dispatchEvent(new CustomEvent('tts_text', { detail: { text: textContent.text } }));
                  // Accumulate raw text, strip tags from full string (tags may span chunks)
                  accumulatedText += textContent.text;
                  currentStatus = null;
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'chart') {
                // Chart - add to accumulated parts and show immediately
                if (parsed.metadata && parsed.metadata.chart_html) {
                  currentStatus = null;
                  accumulatedParts.push({
                    type: 'chart',
                    chartHtml: parsed.metadata.chart_html,
                    chartId: parsed.metadata.chart_id,
                    chartType: parsed.metadata.chart_type
                  });
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'question_card') {
                // Clickable multiple-choice question (ask_user tool)
                const card = parsed.metadata?.question_card;
                if (card) {
                  currentStatus = null;
                  accumulatedParts.push({
                    type: 'question_card',
                    question: card.question,
                    description: card.description,
                    options: card.options,
                    multiple: card.multiple,
                    cardId: card.card_id
                  });
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'form_card') {
                // Single-screen configuration form (present_form tool)
                const card = parsed.metadata?.form_card;
                if (card) {
                  currentStatus = null;
                  accumulatedParts.push({
                    type: 'form_card',
                    title: card.title,
                    description: card.description,
                    fields: card.fields,
                    submitLabel: card.submit_label,
                    formId: card.form_id
                  });
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'message_complete') {
                // Complete message - show immediately (tool results, etc.)
                if (parsed.content && parsed.content[0] && parsed.content[0].text) {
                  accumulatedText = parsed.content[0].text;
                  currentStatus = null;
                  yield {
                    content: [{ type: 'tool-result', text: accumulatedText }]
                  };
                }
              } else if (parsed.type === 'file_download') {
                // File download - add to accumulated parts and show immediately
                if (parsed.metadata && parsed.metadata.file_data && parsed.metadata.filename) {
                  currentStatus = null;
                  accumulatedParts.push({
                    type: 'file_download',
                    filename: parsed.metadata.filename,
                    fileData: parsed.metadata.file_data,
                    format: parsed.metadata.format,
                    sizeBytes: parsed.metadata.size_bytes
                  });
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'local_tool_call') {
                // Local Files: run the tool in the browser (File System Access
                // API) and send the result back on a separate request.
                // Whatever happens inside, the server ALWAYS gets a result —
                // an unhandled throw here would wedge the whole chat stream
                // until the server-side timeout.
                try {
                if (parsed.tool === 'local_write_file') {
                  // Writes go through the confirm flow: build the diff
                  // proposal, show the approval card, wait for the click,
                  // then write only if approved (trusted folders skip the
                  // wait — the card stays as an audit record).
                  console.debug('[local-write] prepare', parsed.call_id);
                  const prep = await prepareLocalWrite(threadIdRef.current, parsed.args);
                  if (!prep.ok) {
                    console.debug('[local-write] prepare failed', prep.error);
                    await sendLocalToolResult(parsed.call_id, prep);
                  } else if (prep.proposal.trusted) {
                    flushTextToParts();
                    currentStatus = null;
                    accumulatedParts.push({
                      type: 'write_confirm',
                      callId: parsed.call_id,
                      ...prep.proposal,
                    });
                    yield { content: buildContentParts() };
                    const outcome = await applyLocalWrite(threadIdRef.current, parsed.args);
                    await sendLocalToolResult(parsed.call_id, outcome);
                  } else {
                    flushTextToParts();
                    currentStatus = null;
                    accumulatedParts.push({
                      type: 'write_confirm',
                      callId: parsed.call_id,
                      ...prep.proposal,
                    });
                    yield { content: buildContentParts() };
                    console.debug('[local-write] waiting decision', parsed.call_id);
                    const decision = await waitForWriteDecision(parsed.call_id, undefined, abortSignal);
                    console.debug('[local-write] decision', parsed.call_id, decision);
                    let outcome;
                    if (decision === null) {
                      outcome = {
                        ok: false,
                        error: 'CLIENT_TIMEOUT: the user did not review the write in time. Ask them to retry and approve the card.',
                      };
                    } else if (!decision.approved) {
                      outcome = {
                        ok: false,
                        error: 'USER_REJECTED: the user rejected this write. Do NOT retry the same write; acknowledge and propose an alternative.',
                      };
                    } else {
                      if (decision.trustFolder) {
                        // Mark the folder trusted — fire-and-forget: this must
                        // NEVER block (or break) the write itself.
                        setThreadFolderTrusted(threadIdRef.current, true)
                          .then(() => onFolderTrustedRef.current?.())
                          .catch((e) =>
                            console.error('[local-write] trust marking failed (non-fatal)', e)
                          );
                      }
                      outcome = await applyLocalWrite(threadIdRef.current, parsed.args);
                      console.debug('[local-write] applied', parsed.call_id, outcome);
                    }
                    await sendLocalToolResult(parsed.call_id, outcome);
                    console.debug('[local-write] result sent', parsed.call_id);
                  }
                } else {
                  const outcome = await executeLocalTool(
                    parsed.tool,
                    parsed.args,
                    threadIdRef.current,
                    abortSignal
                  );
                  await sendLocalToolResult(parsed.call_id, outcome);
                }
                } catch (e) {
                  if (e?.name === 'AbortError') {
                    // Stop pressed: unwind the run immediately. The server
                    // sees the dropped connection and cancels its side; do
                    // NOT post a fake tool error (would wedge the stream for
                    // the full server-side timeout).
                    console.debug('[local] tool flow aborted by user (Stop)');
                    throw e;
                  }
                  console.error('[local] tool flow failed:', e);
                  await sendLocalToolResult(parsed.call_id, {
                    ok: false,
                    error: `CLIENT_ERROR: ${e?.message || 'local tool flow failed'}`,
                  });
                }
              } else if (parsed.type === 'message_ids') {
                setTimeout(() => {
                  const allMessages = document.querySelectorAll('[data-role]');
                  if (allMessages.length >= 2) {
                    const userMsg = allMessages[allMessages.length - 2];
                    const assistantMsg = allMessages[allMessages.length - 1];
                    if (userMsg && parsed.user_message_id) {
                      userMsg.setAttribute('data-message-id', parsed.user_message_id);
                    }
                    if (assistantMsg && parsed.assistant_message_id) {
                      assistantMsg.setAttribute('data-message-id', parsed.assistant_message_id);
                    }
                  }
                }, 100);
                // The server adopts the client-sent thread id, so after the
                // first exchange threadIdRef.current IS the real thread id.
                // Surface it (e.g. the Share button needs currentThreadId).
                if (onThreadId) onThreadId(threadIdRef.current);
                loadThreads(selectedGptId);
              } else if (parsed.type === 'thread_title') {
                if (parsed.thread_id && parsed.title) {
                  onThreadTitle(parsed.thread_id, parsed.title);
                }
              } else if (parsed.type === 'error') {
                // Error from backend - append error text directly
                const errorMessage = parsed.error?.message || 'An error occurred';
                accumulatedText += (accumulatedText ? '\n\n' : '') + `**Error:** ${errorMessage}`;
                yield { content: buildContentParts() };
              } else if (parsed.type === 'done') {
                sawDone = true;
                chatEvents.dispatchEvent(new CustomEvent('tts_stream_end'));
                break;
              }
            } catch (e) {
              console.error('Parse error:', e);
            }
          }
        }

        // Stream ended without the backend's done event → severed (silent
        // end: reader returned done, or server closed the connection).
        if (!sawDone) notifySevered();

        if (activityPart && !activityPart.endedAt) {
          // Close the block on a final yield (not only on the return value)
          activityPart.endedAt = Date.now();
          yield { content: buildContentParts() };
        }
        return { content: buildContentParts() };
      } catch (error) {
        if (error.name === 'AbortError') {
          return { content: [{ type: 'text', text: '' }] };
        }
        // Network-level failure (server down mid-stream, connection reset) —
        // same recovery as a silent stream end.
        if (!sawDone) notifySevered();
        throw error;
      }
    }
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <QuestionAutoSubmitListener />
      {children}
    </AssistantRuntimeProvider>
  );
}

// Synthetic assistant message shown at the top of a thread whose GPT the user
// can no longer use (share revoked after the thread was created).
const buildAssistantUnavailableMessage = (assistant) => ({
  id: 'assistant_unavailable_banner',
  role: 'assistant',
  content: [{
    type: 'text',
    text: `🔒 This conversation was with **${assistant?.name || 'an assistant'}**, which is no longer available to you. ` +
          'You can still read the history below, but sending messages here is disabled. ' +
          'Start a new chat to continue with another assistant.',
  }],
});

export default function ChatInterfaceFull({ onLogout }) {
  // Get API URL from config
  const API_URL = window.INSIGHT_CONFIG?.API_URL ?? '';
  const token = localStorage.getItem('cme_token');

  // State
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('cme_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [company, setCompany] = useState(null);
  const [customGpts, setCustomGpts] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedGptId, setSelectedGptId] = useState('');
  // Default-first: the boot GPT is resolved in loadUserInfo (?gpt= or company
  // default) — never seeded from localStorage.

  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalThreadId, setShareModalThreadId] = useState(null); // Thread the modal operates on (defaults to the current one)
  // Voice state managed by VoiceProvider — see render below

  // Share State
  const [sharedThreads, setSharedThreads] = useState([]); // Threads shared with current user
  const [currentThreadSharedWith, setCurrentThreadSharedWith] = useState([]); // Users the current thread is shared with
  const [isCurrentThreadOwner, setIsCurrentThreadOwner] = useState(true); // Is current user the owner of current thread
  const [companyUsers, setCompanyUsers] = useState([]); // All users in the company for sharing

  // Chat State
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [messageFiles, setMessageFiles] = useState({});
  const threadIdRef = useRef(null);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const currentThreadIdRef = useRef(null); // Always-latest persisted thread id (severed-stream recovery)
  useEffect(() => { currentThreadIdRef.current = currentThreadId; }, [currentThreadId]);
  const isLoadingThreadRef = useRef(false);

  // Incognito chat: hidden from history/search, never fed to memory, not shareable.
  // Ref mirrors state so the runtime's async send reads the CURRENT value.
  const [isIncognitoChat, setIsIncognitoChat] = useState(false);
  const incognitoRef = useRef(false);
  useEffect(() => { incognitoRef.current = isIncognitoChat; }, [isIncognitoChat]);

  // Thread whose assistant the user can no longer use (share revoked after
  // creation): {id, name} — shows a banner and makes the composer read-only.
  const [unavailableAssistant, setUnavailableAssistant] = useState(null);
  const unavailableAssistantRef = useRef(null);
  useEffect(() => { unavailableAssistantRef.current = unavailableAssistant; }, [unavailableAssistant]);
  const [historyMessages, setHistoryMessages] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [runtimeKey, setRuntimeKey] = useState(0); // Used to force runtime recreation

  // Local Files state: { name: string, needsPermission: boolean } | null
  const [localFolder, setLocalFolder] = useState(null);
  // Ref mirror: the runtime wrapper's async send reads the CURRENT folder
  // state at send time (state in the closure would be stale).
  const localFolderRef = useRef(null);
  useEffect(() => {
    localFolderRef.current = localFolder;
  }, [localFolder]);
  const [localFilesBusy, setLocalFilesBusy] = useState(false);

  // Office 365 state: { connected, account_name, account_email } | null
  const [o365, setO365] = useState(null);
  const [o365Busy, setO365Busy] = useState(false);

  // Gmail state: { connected, account_name, account_email } | null
  const [gmail, setGmail] = useState(null);
  const [gmailBusy, setGmailBusy] = useState(false);

  // Check if impersonating
  const isImpersonating = localStorage.getItem('cme_impersonating') === 'true';

  // Office 365: check the current user's connection state
  const loadO365Status = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/o365/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setO365(await res.json());
    } catch {
      /* offline — keep previous state */
    }
  }, [API_URL, token]);

  // Office 365: load status when the GPT has the capability.
  // Same fallback as currentGpt (B19): default GPT when none is selected,
  // so new users see the chip without having to pick a GPT first.
  const o365Enabled = !!(
    customGpts.find((g) => g.id === selectedGptId) ||
    customGpts.find((g) => g.is_default)
  )?.office365_enabled;
  useEffect(() => {
    if (o365Enabled) {
      loadO365Status();
    } else {
      setO365(null);
    }
  }, [o365Enabled, loadO365Status]);

  // Office 365: after the OAuth tab comes back (window regains focus),
  // re-check the status so the chip appears without a manual reload
  useEffect(() => {
    if (!o365Enabled) return undefined;
    const onFocus = () => loadO365Status();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [o365Enabled, loadO365Status]);

  // Office 365: start the OAuth flow (opens Microsoft in a new tab)
  const handleConnectO365 = async () => {
    setO365Busy(true);
    try {
      const res = await fetch(`${API_URL}/api/o365/connect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.authorization_url) {
          window.open(data.authorization_url, '_blank');
        }
      }
    } catch (e) {
      console.error('O365 connect failed:', e);
    } finally {
      setO365Busy(false);
    }
  };

  // Office 365: disconnect one account (empty = the single connected one)
  const handleDisconnectO365 = async (account = null) => {
    setO365Busy(true);
    try {
      await fetch(`${API_URL}/api/o365/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ account: account || '' }),
      });
      setO365(null);
      loadO365Status();
    } finally {
      setO365Busy(false);
    }
  };

  // Gmail: check the current user's connection state
  const loadGmailStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/gmail/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setGmail(await res.json());
    } catch {
      /* offline — keep previous state */
    }
  }, [API_URL, token]);

  // Gmail: load status when the GPT has the capability (same default-GPT
  // fallback as o365Enabled)
  const gmailEnabled = !!(
    customGpts.find((g) => g.id === selectedGptId) ||
    customGpts.find((g) => g.is_default)
  )?.gmail_enabled;
  useEffect(() => {
    if (gmailEnabled) {
      loadGmailStatus();
    } else {
      setGmail(null);
    }
  }, [gmailEnabled, loadGmailStatus]);

  // Gmail: re-check status when the OAuth tab comes back
  useEffect(() => {
    if (!gmailEnabled) return undefined;
    const onFocus = () => loadGmailStatus();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [gmailEnabled, loadGmailStatus]);

  // Gmail: start the OAuth flow (opens Google in a new tab)
  const handleConnectGmail = async () => {
    setGmailBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/gmail/connect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.authorization_url) {
          window.open(data.authorization_url, '_blank');
        }
      }
    } catch (e) {
      console.error('Gmail connect failed:', e);
    } finally {
      setGmailBusy(false);
    }
  };

  // Gmail: disconnect one account (empty = the single connected one)
  const handleDisconnectGmail = async (account = null) => {
    setGmailBusy(true);
    try {
      await fetch(`${API_URL}/api/gmail/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ account: account || '' }),
      });
      setGmail(null);
      loadGmailStatus();
    } finally {
      setGmailBusy(false);
    }
  };

  // Get conversation starters for selected GPT
  const getConversationStarters = () => {
    if (!selectedGptId) return [];
    const gpt = customGpts.find(g => g.id === selectedGptId);
    return gpt?.conversation_starters || [];
  };

  // Local Files: restore the connected folder when switching threads
  // (queryPermission only — never prompts outside a user gesture)
  useEffect(() => {
    let cancelled = false;
    setLocalFolder(null);
    if (!currentThreadId) return undefined;
    getConnectedFolder(currentThreadId)
      .then((res) => {
        if (!cancelled) setLocalFolder(res);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentThreadId]);

  // Local Files: connect / change folder (called from the 📁 chip — user gesture)
  const handleConnectFolder = async () => {
    if (!isLocalFilesSupported()) {
      window.alert('Local folders require Chrome or Edge.');
      return;
    }
    if (!threadIdRef.current) return;
    setLocalFilesBusy(true);
    try {
      const name = await connectFolder(threadIdRef.current);
      if (name) {
        setLocalFolder({ name, needsPermission: false });
        // Persist the binding server-side (best-effort; new threads don't
        // exist server-side yet — binding re-syncs on the next connect)
        fetch(`${API_URL}/cme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            type: 'local_files.set_folder',
            params: { thread_id: threadIdRef.current, folder_name: name },
          }),
        }).catch(() => {});
      }
    } finally {
      setLocalFilesBusy(false);
    }
  };

  // Local Files: disconnect folder (chip ✕)
  const handleDisconnectFolder = async () => {
    setLocalFilesBusy(true);
    try {
      await disconnectFolder(threadIdRef.current);
      setLocalFolder(null);
      fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          type: 'local_files.set_folder',
          params: { thread_id: threadIdRef.current, folder_name: null },
        }),
      }).catch(() => {});
    } finally {
      setLocalFilesBusy(false);
    }
  };

  // Local Files: the run generator (in ChatRuntimeWrapper) reports that the
  // folder was marked trusted (checkbox on the write card) so the chip updates
  const handleFolderTrusted = useCallback(() => {
    setLocalFolder((prev) => (prev ? { ...prev, trusted: true } : prev));
  }, []);

  // Local Files: toggle trusted folder (shield — auto-approved writes)
  const handleToggleTrust = async () => {
    const next = !(localFolder?.trusted);
    setLocalFilesBusy(true);
    try {
      const result = await setThreadFolderTrusted(threadIdRef.current, next);
      setLocalFolder((prev) => (prev ? { ...prev, trusted: result } : prev));
    } finally {
      setLocalFilesBusy(false);
    }
  };

  // Initialize from cached user data (App.jsx fetches fresh data on mount)
  // Returns the boot GPT for use in loading threads:
  //   ?gpt= (explicit "Use" from the Assistants page) or '' — everything else
  //   lands on the company default inside loadCustomGpts (default-first).
  const loadUserInfo = useCallback(async () => {
    try {
      // Read from localStorage (App.jsx already fetched fresh data)
      const stored = localStorage.getItem('cme_user');
      if (!stored) return '';

      const userInfo = JSON.parse(stored);
      setUser(userInfo);

      // Re-apply the appearance from the fresh profile (App.jsx listens)
      window.dispatchEvent(new Event('themeChanged'));

      // Explicit session choice: ?gpt=<id> from the Assistants "Use" button.
      // Consume it once so a refresh goes back to the normal boot rules.
      const urlParams = new URLSearchParams(window.location.search);
      const gptFromUrl = urlParams.get('gpt');
      if (gptFromUrl) {
        urlParams.delete('gpt');
        const rest = urlParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
      }

      // Load company info
      if (userInfo.company_id) {
        const companyRes = await fetch(`${API_URL}/companies/${userInfo.company_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (companyRes.ok) {
          const companyData = await companyRes.json();
          setCompany(companyData);
          document.title = companyData.name;

          // Cache company data for next page load (used by inline script to avoid favicon flicker)
          localStorage.setItem('cme_company', JSON.stringify(companyData));

          // Update favicon and apple-touch-icon
          if (companyData.favicon_url) {
            const faviconUrl = `${API_URL}${companyData.favicon_url}`;
            const favicon = document.getElementById('dynamic-favicon');
            const appleIcon = document.getElementById('dynamic-apple-icon');
            if (favicon) favicon.href = faviconUrl;
            if (appleIcon) appleIcon.href = faviconUrl;
          }
        }
      }

      // Boot GPT: explicit ?gpt= only; everything else lands on the company
      // default inside loadCustomGpts (selection is conversation-scoped).
      const gptId = gptFromUrl || '';
      if (gptId) {
        setSelectedGptId(gptId);
      } else {
        setSelectedGptId('');
      }

      return gptId;
    } catch (error) {
      console.error('Failed to load user info:', error);
      return '';
    }
  }, [API_URL, token]);

  // Load custom GPTs
  const loadCustomGpts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/custom-gpts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const gpts = await response.json();
        setCustomGpts(gpts);

        // Drop stale selections (assistant deleted or unshared): otherwise new
        // chats are silently created on a ghost GPT and degrade to a generic
        // assistant. Fall back to the company default.
        setSelectedGptId(prev => {
          if (prev && gpts.some(g => g.id === prev)) return prev;
          const fallback = gpts.find(g => g.is_default) || gpts[0];
          return fallback ? fallback.id : '';
        });
      }
    } catch (error) {
      console.error('Failed to load custom GPTs:', error);
    }
  }, [API_URL, token]);

  // Load thread history (filtered by selected GPT)
  // Note: gptId must be passed explicitly - we don't use selectedGptId from closure
  // to avoid recreating this callback when selectedGptId changes
  const loadThreads = useCallback(async (gptId = '') => {
    try {
      const params = { limit: 50 };
      // Always show ALL chats (assistant name is displayed per-item in the sidebar)
      params.custom_gpt_id = 'all';

      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.list',
          params
        })
      });

      if (response.ok) {
        const result = await response.json();
        setThreads(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load threads:', error);
    }
  }, [API_URL, token]);

  // Live-update a thread title in the sidebar (e.g. LLM-generated title event)
  const handleThreadTitle = useCallback((threadId, title) => {
    setThreads(prev => prev.map(t => (t.id === threadId ? { ...t, title } : t)));
  }, []);

  // Adopt the real thread id once the first exchange creates the thread —
  // the Share button (and sidebar highlight) depend on currentThreadId.
  const handleThreadId = useCallback((threadId) => {
    setCurrentThreadId(prev => (prev === threadId ? prev : threadId));
  }, []);

  // Load threads shared with current user
  const loadSharedThreads = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.shared',
          params: {}
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSharedThreads(result.threads || []);
      }
    } catch (error) {
      console.error('Failed to load shared threads:', error);
    }
  }, [API_URL, token]);

  // Load participants for a thread (who it's shared with)
  const loadThreadParticipants = useCallback(async (threadId) => {
    if (!threadId) {
      setCurrentThreadSharedWith([]);
      setIsCurrentThreadOwner(true);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.participants',
          params: { thread_id: threadId }
        })
      });

      if (response.ok) {
        const result = await response.json();
        setCurrentThreadSharedWith(result.participants || []);
        setIsCurrentThreadOwner(result.is_owner ?? true);
      }
    } catch (error) {
      console.error('Failed to load thread participants:', error);
      setCurrentThreadSharedWith([]);
      setIsCurrentThreadOwner(true);
    }
  }, [API_URL, token]);

  // Load thread messages and convert to assistant-ui format
  const loadThreadMessages = useCallback(async (threadId) => {
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.get',
          params: { thread_id: threadId, limit: 100 }
        })
      });

      if (!response.ok) {
        console.error('Failed to load thread:', response.status);
        return { messages: [], gptId: null, files: {}, assistant: null };
      }

      const thread = await response.json();
      const items = thread.items?.data || [];
      const threadIncognito = !!thread.incognito;

      // Extract files per user message, keyed by message id (for messageFiles context)
      const userMessageFiles = {};

      // Convert to ThreadMessageLike format with metadata for charts
      const messages = items.map(msg => {
        // Preserve content structure - can be string or array of parts (text, image, etc.)
        let content = msg.content;

        // Track attachments for user messages (for messageFiles context used by real-time messages)
        const attachments = msg.attachments || [];
        if (msg.role === 'user') {
          userMessageFiles[msg.id] = attachments;
        }

        return {
          id: msg.id,
          role: msg.role,
          content: content, // Preserve original structure (string or array with images)
          metadata: msg.metadata, // Include metadata for chart_html
          attachments: attachments, // Include attachments for history rendering
          createdAt: msg.created_at ? new Date(msg.created_at) : undefined,
          user_id: msg.user_id, // For showing message author in shared threads
        };
      });

      return { messages, gptId: thread.custom_gpt_id, files: userMessageFiles, assistant: thread.assistant || null, incognito: threadIncognito };
    } catch (error) {
      console.error('Error loading thread messages:', error);
      return { messages: [], gptId: null, files: {}, assistant: null, incognito: false };
    }
  }, [API_URL, token]);

  // Initial load
  useEffect(() => {
    if (!token) {
      onLogout?.();
      return;
    }

    const initialize = async () => {
      // Load user info first (resolves the boot GPT: ?gpt= or company default)
      const gptId = await loadUserInfo();
      loadCustomGpts();
      loadSharedThreads(); // Load threads shared with this user

      // Check for thread ID in URL
      const urlParams = new URLSearchParams(window.location.search);
      const threadIdFromUrl = urlParams.get('thread');

      if (threadIdFromUrl) {
        // Load existing thread
        isLoadingThreadRef.current = true;
        const { messages, gptId: threadGptId, files, assistant, incognito: threadIncognito } = await loadThreadMessages(threadIdFromUrl);

        threadIdRef.current = threadIdFromUrl;
        setCurrentThreadId(threadIdFromUrl);
        setIsIncognitoChat(threadIncognito);
        const assistantUnavailable = !!assistant && assistant.accessible === false;
        setUnavailableAssistant(assistantUnavailable ? assistant : null);
        setHistoryMessages(
          assistantUnavailable
            ? [buildAssistantUnavailableMessage(assistant), ...messages]
            : messages
        );
        setMessageFiles(files || {}); // Restore attachments from loaded thread
        loadThreadParticipants(threadIdFromUrl); // Load who this thread is shared with

        // Use thread's GPT if available (an open conversation keeps its
        // assistant). Skip switching when the thread's assistant is no longer
        // accessible (selecting it would mislabel the chat with the default).
        const effectiveGptId = threadGptId || gptId;
        if (threadGptId && !assistantUnavailable) {
          setSelectedGptId(threadGptId);
        }

        // Load threads for the effective GPT
        loadThreads(effectiveGptId);
        isLoadingThreadRef.current = false;
      } else {
        // Start with a new thread
        threadIdRef.current = crypto.randomUUID();
        setHistoryMessages([]);
        setIsIncognitoChat(false);
        // Load threads for user's last selected GPT
        loadThreads(gptId);
      }

      setIsLoadingHistory(false);
    };

    initialize();
  }, [token, loadUserInfo, loadCustomGpts, loadThreads, loadThreadMessages, loadSharedThreads, loadThreadParticipants]);

  // Handle GPT selection — conversation-scoped (not persisted: a new chat or
  // the next page load goes back to the company default)
  const handleSelectGPT = async (gptId) => {
    // Locked companies: the chip is informative only — no free selection
    if (company?.lock_gpt_selection) return;

    const newGptId = gptId || null;

    if (selectedGptId !== newGptId) {
      setSelectedGptId(newGptId || '');

      // Load threads for this GPT
      loadThreads(newGptId || '');

      // Start new chat when switching GPTs — keepGpt: the switch itself IS
      // the explicit selection, the default-first reset must not wipe it
      if (!isLoadingThreadRef.current) {
        startNewChat(false, { keepGpt: true });
      }
    }
  };

  // Handle new chat (incognito = false by default; each new chat is regular)
  // keepGpt: skip the default-first reset — used when the new chat is the
  // direct consequence of picking an assistant (chip selection or "Use").
  const startNewChat = (incognito = false, { keepGpt = false } = {}) => {
    // Clear history and start fresh - DOM manipulation
    threadIdRef.current = crypto.randomUUID();
    setCurrentThreadId(null);
    setHistoryMessages([]);
    setMessageFiles({});
    setSelectedFiles([]); // Drop pending attachments — they belong to the old conversation
    setCurrentThreadSharedWith([]); // Reset sharing state
    setIsCurrentThreadOwner(true); // User owns new threads
    setUnavailableAssistant(null); // New chat always uses an accessible assistant
    setIsIncognitoChat(incognito);
    setRuntimeKey(prev => prev + 1); // Force runtime recreation

    // Default-first: a new chat starts on the default assistant. An explicit
    // pick (chip or "Use") covers the conversation it opened, not the session.
    if (!keepGpt) {
      const fallback = customGpts.find(g => g.is_default) || customGpts[0];
      if (fallback && fallback.id !== selectedGptId) {
        setSelectedGptId(fallback.id);
        loadThreads(fallback.id);
      }
    }
  };

  const handleNewChat = () => startNewChat(false);

  // Incognito toggle: clicking "Incognito Chat" while ALREADY in an incognito
  // chat exits it (new regular chat); otherwise starts a new incognito chat.
  const handleToggleIncognitoChat = () => startNewChat(!isIncognitoChat);

  // Handle thread selection (also reloads if clicking same thread)
  const handleSelectThread = async (threadId) => {
    // Load thread messages - DOM manipulation, no URL changes
    isLoadingThreadRef.current = true;

    const { messages, gptId, files, assistant, incognito: threadIncognito } = await loadThreadMessages(threadId);

    threadIdRef.current = threadId;
    setCurrentThreadId(threadId);
    setIsIncognitoChat(threadIncognito);
    const assistantUnavailable = !!assistant && assistant.accessible === false;
    setUnavailableAssistant(assistantUnavailable ? assistant : null);
    setHistoryMessages(
      assistantUnavailable
        ? [buildAssistantUnavailableMessage(assistant), ...messages]
        : messages
    );
    setMessageFiles(files || {}); // Restore attachments from loaded thread
    setSelectedFiles([]); // Drop pending attachments from the previous thread
    setRuntimeKey(prev => prev + 1); // Force runtime recreation
    loadThreadParticipants(threadId); // Load who this thread is shared with

    // Only adopt the thread's assistant if it is accessible AND still exists
    // (ghost GPTs must not leak into new chats via selectedGptId).
    if (gptId && !assistantUnavailable && customGpts.some(g => g.id === gptId)) {
      setSelectedGptId(gptId);
    }

    isLoadingThreadRef.current = false;
  };

  // Severed-stream recovery: the SSE ended without the backend's done event
  // (server restart/crash/network drop). The live partial message is stale —
  // the full response may already be persisted. Re-fetch the current thread
  // so the DB truth replaces the frozen "Processing..." state.
  const handleSelectThreadRef = useRef(null);
  useEffect(() => { handleSelectThreadRef.current = handleSelectThread; });
  useEffect(() => {
    let timer = null;
    const onStreamSevered = () => {
      const tid = currentThreadIdRef.current;
      if (!tid || isLoadingThreadRef.current) return; // new chat or already loading
      // Small delay: give a restarting backend a beat, and let the runtime
      // finalize the interrupted message first.
      clearTimeout(timer);
      timer = setTimeout(() => {
        handleSelectThreadRef.current?.(currentThreadIdRef.current);
      }, 1200);
    };
    chatEvents.addEventListener('stream_severed', onStreamSevered);
    return () => {
      clearTimeout(timer);
      chatEvents.removeEventListener('stream_severed', onStreamSevered);
    };
  }, []);

  // Handle thread rename
  const handleRenameThread = async (threadId, newTitle) => {
    try {
      await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.update',
          params: {
            thread_id: threadId,
            title: newTitle
          }
        })
      });
      await loadThreads(selectedGptId);
    } catch (error) {
      console.error('Failed to rename thread:', error);
    }
  };

  // Handle thread delete
  const handleDeleteThread = async (threadId) => {
    try {
      await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.delete',
          params: { thread_id: threadId }
        })
      });

      // If the deleted thread is the currently selected one, start a new chat
      if (threadId === currentThreadId) {
        handleNewChat();
      }

      // Remove the thread from local state (preserves scroll position)
      setThreads(prev => prev.filter(t => t.id !== threadId));
    } catch (error) {
      console.error('Failed to delete thread:', error);
    }
  };

  // Handle thread pin/unpin
  const handlePinThread = async (threadId) => {
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.pin',
          params: { thread_id: threadId }
        })
      });

      if (response.ok) {
        // Reload threads to get updated order (pinned first)
        await loadThreads(selectedGptId);
      }
    } catch (error) {
      console.error('Failed to pin/unpin thread:', error);
    }
  };

  // Load company users for sharing
  const loadCompanyUsers = async () => {
    if (!user?.company_id) return;
    try {
      const response = await fetch(`${API_URL}/companies/${user.company_id}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const users = await response.json();
        setCompanyUsers(users);
      }
    } catch (error) {
      console.error('Failed to load company users:', error);
    }
  };

  // Check if a user has access to a GPT
  const userHasGptAccess = (userId, gpt) => {
    if (!gpt) return true; // No GPT = default chat, everyone has access
    if (gpt.user_id === userId) return true; // Owner always has access
    if (gpt.is_shared) return true; // Shared with all company users
    if (gpt.shared_with_users?.includes(userId)) return true; // Specifically shared
    return false;
  };

  // Load company users when viewing a shared thread (so we can show author names)
  useEffect(() => {
    const isSharedThread = !isCurrentThreadOwner || currentThreadSharedWith.length > 0;
    if (isSharedThread && companyUsers.length === 0) {
      loadCompanyUsers();
    }
  }, [isCurrentThreadOwner, currentThreadSharedWith]);

  // Handle opening share modal — for the current thread (top-bar button)
  // or any OWNED thread from the history menu (threadId param).
  const handleOpenShareModal = async (threadId = null) => {
    const targetId = threadId || currentThreadId;
    if (isIncognitoChat && !threadId) return; // incognito chats are never shareable
    setShareModalThreadId(targetId);
    setShareModalOpen(true);
    // Load company users if not already loaded
    if (companyUsers.length === 0) {
      await loadCompanyUsers();
    }
    // Refresh participants list for the target thread
    if (targetId) {
      await loadThreadParticipants(targetId);
    }
  };

  // Get users who don't have access to the current GPT (for disabling in share modal)
  // Resolve the current GPT: the selected one, or the company default as fallback.
  // Without the fallback, a user who never picked a GPT gets null → all GPT-dependent
  // UI flags (upload, skills) are silently disabled.
  const currentGpt = customGpts.find(g => g.id === selectedGptId) || customGpts.find(g => g.is_default) || null;

  // Active GPT shown in the main chat (selected GPT, or company default, or null=General)
  const activeGpt = (selectedGptId && currentGpt) || customGpts.find(g => g.is_default) || null;
  const activeModelName = activeGpt?.name || 'General';

  // Build the model icon exactly like the sidebar GPTSelector (favicon or gradient avatar)
  const activeModelIcon = useMemo(() => {
    const isDefault = !activeGpt || activeGpt.is_default;
    if (activeGpt?.favicon_url) {
      return (
        <Avatar
          src={`${API_URL}${activeGpt.favicon_url}?t=${Date.now()}`}
          alt={activeModelName}
          sx={{ width: 20, height: 20 }}
        />
      );
    }
    return (
      <Avatar sx={{
        width: 20,
        height: 20,
        background: isDefault
          ? 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)'
          : 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
      }}>
        {isDefault
          ? <ChatBubbleOutlineIcon sx={{ fontSize: 12, color: 'white' }} />
          : <BoltIcon sx={{ fontSize: 12, color: 'white' }} />}
      </Avatar>
    );
  }, [activeGpt, activeModelName, API_URL]);

  const usersWithoutGptAccess = selectedGptId && currentGpt
    ? companyUsers.filter(u => !userHasGptAccess(u.id, currentGpt)).map(u => u.id)
    : [];

  // Share-modal target: the thread the modal is operating on (from the top-bar
  // Share button this is the current thread; from the history menu it can be
  // any owned thread). GPT-dependent props follow the TARGET thread's GPT.
  const shareTargetId = shareModalThreadId || currentThreadId;
  const shareTargetThread = (shareTargetId === currentThreadId ? threads : threads.concat(sharedThreads))
    .find(t => t.id === shareTargetId) || sharedThreads.find(t => t.id === shareTargetId) || null;
  const shareTargetGpt = customGpts.find(g => g.id === shareTargetThread?.custom_gpt_id) || currentGpt;
  const shareTargetUsersWithoutAccess = shareTargetGpt
    ? companyUsers.filter(u => !userHasGptAccess(u.id, shareTargetGpt)).map(u => u.id)
    : [];

  // Handle sharing a thread with a user
  const handleShareThread = async (userId) => {
    const threadId = shareTargetId;
    if (!threadId) return;
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.share',
          params: {
            thread_id: threadId,
            user_id: userId
          }
        })
      });
      if (response.ok) {
        // Refresh the shared-with list for the target thread
        const userToAdd = companyUsers.find(u => u.id === userId);
        if (userToAdd) {
          setCurrentThreadSharedWith(prev => [...prev, userToAdd]);
        }
        loadThreads(); // refresh shared_count badge in the history list
      }
    } catch (error) {
      console.error('Failed to share thread:', error);
    }
  };

  // Handle removing access from a thread
  const handleRemoveThreadAccess = async (userId) => {
    const threadId = shareTargetId;
    if (!threadId) return;
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.unshare',
          params: {
            thread_id: threadId,
            user_id: userId
          }
        })
      });
      if (response.ok) {
        setCurrentThreadSharedWith(prev => prev.filter(u => u.id !== userId));
        loadThreads(); // refresh shared_count badge in the history list
      }
    } catch (error) {
      console.error('Failed to remove thread access:', error);
    }
  };

  // Handle leaving a shared thread (user removes themselves)
  const handleLeaveThread = async (threadId) => {
    if (!threadId || !user?.id) return;
    try {
      const response = await fetch(`${API_URL}/cme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'threads.unshare',
          params: {
            thread_id: threadId,
            user_id: user.id
          }
        })
      });
      if (response.ok) {
        // Remove from shared threads list
        setSharedThreads(prev => prev.filter(t => t.id !== threadId));
        // If the left thread is currently selected, start a new chat
        if (threadId === currentThreadId) {
          handleNewChat();
        }
      }
    } catch (error) {
      console.error('Failed to leave thread:', error);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('cme_token');
    localStorage.removeItem('cme_user');
    localStorage.removeItem('cme_original_token');
    localStorage.removeItem('cme_impersonating');
    onLogout?.();
  };

  // Handle return to super admin
  const handleReturnToSuperAdmin = () => {
    const originalToken = localStorage.getItem('cme_original_token');
    if (originalToken) {
      localStorage.setItem('cme_token', originalToken);
      localStorage.removeItem('cme_original_token');
      localStorage.removeItem('cme_impersonating');
      window.location.href = 'super-admin.html';
    }
  };

  // Handle conversation starter click
  const handleStarterClick = (prompt) => {
    // Find the composer input and fill it
    const textarea = document.querySelector('[placeholder="Type your message..."]');
    if (textarea) {
      // Set value
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        'value'
      ).set;
      nativeInputValueSetter.call(textarea, prompt);

      // Trigger input event
      textarea.dispatchEvent(new Event('input', { bubbles: true }));

      // Submit the form
      setTimeout(() => {
        const form = textarea.closest('form');
        if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }, 100);
    }
  };

  // Welcome guide: shows automatically on empty chat (default GPT only) until
  // the user closes it with the X (permanent dismissal, per user); the "Guide"
  // button re-opens it at any time (each click bumps a tick → remount + scroll
  // to top).
  const [welcomeReopenTick, setWelcomeReopenTick] = useState(0);
  const welcomeForced = welcomeReopenTick > 0;
  const welcomeStorageKey = `cme_welcome_dismissed_${user?.id || 'default'}_v1`;
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    try {
      return !!localStorage.getItem(welcomeStorageKey);
    } catch {
      return false;
    }
  });
  // Re-check when the user profile finishes loading (the key depends on user id)
  useEffect(() => {
    try {
      setWelcomeDismissed(!!localStorage.getItem(welcomeStorageKey));
    } catch {
      /* private mode */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  const showWelcomeSlot = !!currentGpt?.is_default && (welcomeForced || !welcomeDismissed);
  const handleDismissWelcome = () => {
    try {
      localStorage.setItem(welcomeStorageKey, '1');
    } catch {
      /* private mode */
    }
    setWelcomeDismissed(true);
    setWelcomeReopenTick(0); // close any forced (Guide-button) view too
  };

  // Show loading state while loading history
  if (isLoadingHistory) {
    return (
      <Box
        className="app-container"
        sx={{
          bgcolor: 'background.default',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <FilesContext.Provider value={{ selectedFiles, setSelectedFiles }}>
      <MessageFilesContext.Provider value={{ messageFiles }}>
        <Box className="app-container" sx={{ bgcolor: 'background.default' }}>
            {/* Sidebar */}
            <Sidebar
              isOpen={sidebarOpen}
              isCollapsed={sidebarCollapsed}
              onClose={() => setSidebarOpen(false)}
              onCollapse={() => setSidebarCollapsed(true)}
              onExpand={() => setSidebarCollapsed(false)}
              onNewChat={handleNewChat}
              onNewIncognitoChat={handleToggleIncognitoChat}
              isIncognitoActive={isIncognitoChat}
              onSearchChats={() => setSearchModalOpen(true)}
              onSelectThread={handleSelectThread}
              threads={threads}
              sharedThreads={sharedThreads}
              currentThreadId={currentThreadId}
              customGpts={customGpts}
              user={user}
              onOpenProfile={() => setProfileModalOpen(true)}
              onLogout={handleLogout}
              onOpenCompanyAdmin={() => window.location.href = '/company-admin.html'}
              apiUrl={API_URL}
              onRenameThread={handleRenameThread}
              onDeleteThread={handleDeleteThread}
              onLeaveThread={handleLeaveThread}
              onPinThread={handlePinThread}
              onShareThread={(threadId) => handleOpenShareModal(threadId)}
            />

            {/* Chat runtime: recreated (key={runtimeKey}) on every thread switch / new
                chat. The Sidebar stays OUTSIDE it on purpose: it does not use the
                runtime, and keeping it mounted preserves its scroll position, tab
                and hover state across thread switches. */}
            <ChatRuntimeWrapper
              key={runtimeKey}
              threadIdRef={threadIdRef}
              selectedGptId={selectedGptId}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              setMessageFiles={setMessageFiles}
              loadThreads={loadThreads}
              API_URL={API_URL}
              token={token}
              onFolderTrusted={handleFolderTrusted}
              localFolderRef={localFolderRef}
              onThreadTitle={handleThreadTitle}
              onThreadId={handleThreadId}
              unavailableAssistantRef={unavailableAssistantRef}
              incognitoRef={incognitoRef}
            >

            {/* Main Content */}
            <Box
              component="main"
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <Header
                onOpenMobileSidebar={() => setSidebarOpen(true)}
                company={company}
                isImpersonating={isImpersonating}
                onReturnToSuperAdmin={handleReturnToSuperAdmin}
                userRole={user?.role}
                logoMinWidth="1750px"
                currentThreadId={currentThreadId}
                threadSharedWith={currentThreadSharedWith}
                isThreadOwner={isCurrentThreadOwner}
                onShareClick={handleOpenShareModal}
                isIncognitoChat={isIncognitoChat}
                onIncognitoClick={handleToggleIncognitoChat}
              />

              {/* Chat Area */}
              <div
                className="chat-container"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                }}
              >
                <VoiceProvider apiUrl={API_URL} gptId={currentGpt?.id || ''}>
                <EmbeddedConfigContext.Provider value={{
                  hideFileUpload: !(currentGpt?.enable_temporary_upload),
                  hasVoice: !!currentGpt?.voice_config,
                  hasMicrophone: !!currentGpt?.microphone_config,
                  isReadonly: !!unavailableAssistant,
                }}>
                  <LocalFilesContext.Provider value={{
                    enabled: !!currentGpt?.local_files_enabled,
                    folderName: localFolder?.name ?? null,
                    needsPermission: !!localFolder?.needsPermission,
                    trusted: !!localFolder?.trusted,
                    busy: localFilesBusy,
                    onConnect: handleConnectFolder,
                    onDisconnect: handleDisconnectFolder,
                    onToggleTrust: handleToggleTrust,
                  }}>
                  <Office365Context.Provider value={{
                    enabled: !!currentGpt?.office365_enabled,
                    connected: !!o365?.connected,
                    accountName: o365?.account_name || o365?.account_email || null,
                    accounts: o365?.accounts || [],
                    busy: o365Busy,
                    onConnect: handleConnectO365,
                    onDisconnect: handleDisconnectO365,
                  }}>
                  <GmailContext.Provider value={{
                    enabled: !!currentGpt?.gmail_enabled,
                    connected: !!gmail?.connected,
                    accountName: gmail?.account_name || gmail?.account_email || null,
                    accounts: gmail?.accounts || [],
                    busy: gmailBusy,
                    onConnect: handleConnectGmail,
                    onDisconnect: handleDisconnectGmail,
                  }}>
                  <GptSelectorContext.Provider value={{
                    gpts: customGpts.map(g => ({
                      id: g.id,
                      name: g.name,
                      favicon_url: g.favicon_url ? `${API_URL}${g.favicon_url}` : null,
                      is_default: g.is_default,
                    })),
                    selectedId: selectedGptId,
                    onSelect: (id) => handleSelectGPT(id),
                    locked: !!company?.lock_gpt_selection,
                  }}>
                  <SharedThreadContext.Provider value={{
                    isSharedThread: !isCurrentThreadOwner || currentThreadSharedWith.length > 0,
                    companyUsers,
                    currentUserId: user?.id,
                  }}>
                    <IncognitoContext.Provider value={isIncognitoChat}>
                    <Thread
                      historyMessages={historyMessages}
                      activeModelName={activeModelName}
                      activeModelIcon={activeModelIcon}
                      onNewChat={handleNewChat}
                      welcomeSlot={
                        showWelcomeSlot ? (
                          <WelcomeGuide
                            gptName={currentGpt?.name}
                            gptLogo={
                              (currentGpt?.logo_url || currentGpt?.favicon_url)
                                ? `${API_URL}${currentGpt.logo_url || currentGpt.favicon_url}`
                                : null
                            }
                            onSelectPrompt={handleStarterClick}
                            onDismiss={handleDismissWelcome}
                          />
                        ) : null
                      }
                      forceWelcome={welcomeForced}
                      welcomeReopenKey={welcomeReopenTick}
                      onWelcomeReopen={currentGpt?.is_default ? () => setWelcomeReopenTick((n) => n + 1) : null}
                      conversationStartersSlot={
                        <ConversationStarters
                          starters={getConversationStarters()}
                          onSelectStarter={handleStarterClick}
                        />
                      }
                     />
                    </IncognitoContext.Provider>
                    </SharedThreadContext.Provider>
                   </GptSelectorContext.Provider>
                   </GmailContext.Provider>
                   </Office365Context.Provider>
                   </LocalFilesContext.Provider>
                </EmbeddedConfigContext.Provider>
                </VoiceProvider>
              </div>
            </Box>

            {/* Modals */}
            <ProfileModal
              isOpen={profileModalOpen}
              onClose={() => setProfileModalOpen(false)}
              user={user}
              apiUrl={API_URL}
              onUserUpdate={setUser}
            />

            <SearchChatsModal
              isOpen={searchModalOpen}
              onClose={() => setSearchModalOpen(false)}
              onSelectThread={handleSelectThread}
              apiUrl={API_URL}
            />

            <ShareModal
              isOpen={shareModalOpen}
              onClose={() => setShareModalOpen(false)}
              threadId={shareTargetId}
              threadTitle={shareTargetThread?.title}
              sharedWith={currentThreadSharedWith}
              isOwner={shareModalThreadId && shareModalThreadId !== currentThreadId
                ? true // opened from the history menu: owned threads only
                : isCurrentThreadOwner}
              companyUsers={companyUsers}
              currentUserId={user?.id}
              onShare={handleShareThread}
              onRemoveAccess={handleRemoveThreadAccess}
              usersWithoutGptAccess={shareTargetUsersWithoutAccess}
              gptName={shareTargetGpt?.name}
            />
            </ChatRuntimeWrapper>
        </Box>

          <style>{`
            /* No animation on messages to prevent scroll jump issues */
            .chat-message-enter {
              /* Animation disabled - was causing scroll jump when new messages are added */
            }

            .chat-message-bubble {
              transition: all 0.2s ease-out;
            }

            /* Hide message tools by default */
            .message-tools-container {
              opacity: 0 !important;
              visibility: visible !important;
              transition: opacity 0.2s ease-out;
            }

            /* Show on hover (desktop) */
            [data-role]:hover .message-tools-container {
              opacity: 1 !important;
            }

            /* Sidebar styling */
            #sidebar {
              height: 100%;
              overflow: hidden;
            }

            #historyScroll {
              flex: 1;
              min-height: 0;
            }
            /* History scrollbar styles are in nnaico.css */
          `}</style>

      </MessageFilesContext.Provider>
    </FilesContext.Provider>
  );
}
